import React, { useEffect, useState } from 'react';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import moment from 'moment';
import classNames from 'classnames';
import Button from 'components/v2/buttons/Button';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import accounting from 'accounting';
import { useTranslation } from 'react-i18next';
import { FSC_CALCULATION_FREQUENCY, FSC_CONFIG_ACCOUNT_TYPE, FSC_EFFECTIVE_DAY, FSC_EXPORT_ANCHOR_EVENTS, FSC_IMPORT_ANCHOR_EVENTS, FSC_REPOSITION_ANCHOR_EVENTS, FSC_SOURCE, FSC_TYPE, FUEL_PRICE_WEEK, } from 'constants/fsc/fsc';
import { FSC_CALCULATION_FREQUENCY_SLUGS, FSC_EFFECTIVE_DAY_SLUGS, FSC_EXPORT_ANCHOR_EVENTS_SLUGS, FSC_IMPORT_ANCHOR_EVENTS_SLUGS, FSC_REPOSITION_ANCHOR_EVENTS_SLUGS, FSC_SOURCE_SLUGS, FSC_TYPE_SLUGS, FUEL_PRICE_WEEK_SLUGS, } from 'constants/fsc/slugs';
import Panel from 'components/v2/Panel';
import Alert from 'components/ui/Alert';
import FSCPreview from './FSCPreview';
import userApiUtils from 'utils/UserWebAPIUtils';
import { isCarrier, isShipper } from 'helpers/roles';
const flatFuelSurchargeTypes = [FSC_TYPE.FLAT_AMOUNT, FSC_TYPE.FLAT_PERCENT];
const variableFuelSurchargeTypes = [
    FSC_TYPE.VARIABLE_PERCENT,
    FSC_TYPE.VARIABLE_AMOUNT_PER_MILE,
];
const getSlug = (valueList, slugList, value) => {
    const key = Object.keys(valueList).find((key) => valueList[key] === value);
    return slugList[key];
};
export default function FSCSettingsSummary({ data, accountId, defaultOpen }) {
    const { t } = useTranslation();
    const [customerType, setCustomerType] = useState('');
    if (!data) {
        return null;
    }
    useEffect(() => {
        async function getUserAndSetCustomerType() {
            const user = await userApiUtils.getV2(accountId);
            if (isCarrier(user)) {
                setCustomerType(FSC_CONFIG_ACCOUNT_TYPE.CARRIER);
            }
            else if (isShipper(user)) {
                setCustomerType(FSC_CONFIG_ACCOUNT_TYPE.SHIPPER);
            }
        }
        getUserAndSetCustomerType().catch(console.error);
    }, [accountId]);
    function shouldRenderViewMore() {
        return isVariableFSCType();
    }
    function renderViewMore(open) {
        const text = open ? t('HIDE') : t('VIEW');
        return (React.createElement("div", { className: "flex-shrink-0 flex-grow-0 lg:absolute lg:top-0 lg:right-0" },
            React.createElement(Button, { className: classNames('flex-grow-0 flex-shrink-0'), testDataId: "view-more-or-hide-button" }, text)));
    }
    function renderHeading(open) {
        return (React.createElement("div", { className: "flex items-start justify-end space-x-3 flex-1 relative mb-4 text-left" },
            React.createElement("div", { className: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full" },
                React.createElement(DescriptionListItem, { label: t('VALIDITY_START_DATE'), className: "", dataTestId: "validity-start-date" }, moment(data.validityStartDate).format('MM/DD/YYYY')),
                React.createElement(DescriptionListItem, { label: t('VALIDITY_END_DATE'), className: "", dataTestId: "validity-end-date" }, moment(data.validityEndDate).format('MM/DD/YYYY')),
                React.createElement(DescriptionListItem, { label: t('FUEL_SURCHARGE_TYPE'), className: "", dataTestId: "fsc-type" }, t(getSlug(FSC_TYPE, FSC_TYPE_SLUGS, data.fscType))),
                renderFlatFSCFields()),
            shouldRenderViewMore() && renderViewMore(open)));
    }
    function isFlatFSCType() {
        return flatFuelSurchargeTypes.includes(data.fscType);
    }
    function isVariableFSCType() {
        return variableFuelSurchargeTypes.includes(data.fscType);
    }
    function formatAmount(value) {
        if (!value) {
            return '-';
        }
        return accounting.formatMoney(value);
    }
    function formatFsc(value, valueUnit) {
        if (!value) {
            return '-';
        }
        if (valueUnit === '$') {
            return `${valueUnit}${accounting.formatNumber(value, 2)}`;
        }
        return `${accounting.formatNumber(value, 2)}${valueUnit}`;
    }
    function formatValue(value, valueUnit) {
        if (valueUnit) {
            if (valueUnit === t('COST_PER_MILE')) {
                return t('COST_PER_MILE_VALUE', {
                    value: accounting.formatNumber(value, 2),
                });
            }
            return formatFsc(value, valueUnit);
        }
        return formatAmount(value);
    }
    function renderValueField(label, value, className, dataTestId, valueUnit = null) {
        return (React.createElement(DescriptionListItem, { label: label, className: className, dataTestId: dataTestId }, formatValue(value, valueUnit)));
    }
    function renderFlatFSCFields() {
        if (!isFlatFSCType()) {
            return null;
        }
        const isFlatAmount = data.fscType === FSC_TYPE.FLAT_AMOUNT;
        return renderValueField(`${t('FUEL_SURCHARGE')} ${isFlatAmount ? '($)' : '(%)'}`, data.fscAmountCost || data.fscAmountPercent, 'mb-4 lg:mt-0', 'fuel-surcharge', isFlatAmount ? '$' : '%');
    }
    function renderVariableFSCFields() {
        if (!isVariableFSCType()) {
            return null;
        }
        const isShipper = customerType === FSC_CONFIG_ACCOUNT_TYPE.SHIPPER;
        const unit = data.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE
            ? t('COST_PER_MILE')
            : '%';
        const unitLabel = data.fscType === FSC_TYPE.VARIABLE_AMOUNT_PER_MILE
            ? `(${t('COST_PER_MILE')})`
            : '(%)';
        return (React.createElement("div", { className: "space-y-4" },
            React.createElement("div", { className: "mb-4" },
                React.createElement("h3", { className: "mb-4" }, t('ASSESSMENT_SETTINGS')),
                React.createElement("div", { className: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" },
                    React.createElement(DescriptionListItem, { label: t('MATRIX_SOURCE'), className: "mb-4", dataTestId: "matrix-source" }, t(getSlug(FSC_SOURCE, FSC_SOURCE_SLUGS, data.matrixSource))),
                    React.createElement(DescriptionListItem, { label: t('CALCULATION_FREQUENCY'), className: "mb-4", dataTestId: "calculation-frequency" }, t(getSlug(FSC_CALCULATION_FREQUENCY, FSC_CALCULATION_FREQUENCY_SLUGS, data.calculationFrequency))),
                    data.calculationFrequency !==
                        FSC_CALCULATION_FREQUENCY.MONTHLY && (React.createElement(React.Fragment, null,
                        React.createElement(DescriptionListItem, { label: t('FUEL_PRICE_WEEK'), className: "mb-4", dataTestId: "fuel-price-week" }, t(getSlug(FUEL_PRICE_WEEK, FUEL_PRICE_WEEK_SLUGS, data.fuelPriceWeek))),
                        React.createElement(DescriptionListItem, { label: t('EFFECTIVE_DAY'), className: "mb-4", dataTestId: "effetive-day" }, t(getSlug(FSC_EFFECTIVE_DAY, FSC_EFFECTIVE_DAY_SLUGS, data.effectiveDay))))),
                    isShipper && (React.createElement(React.Fragment, null,
                        React.createElement(DescriptionListItem, { label: t('ANCHOR_EVENT_IMPORT'), className: "mb-4", dataTestId: "anchor-event-import" }, t(getSlug(FSC_IMPORT_ANCHOR_EVENTS, FSC_IMPORT_ANCHOR_EVENTS_SLUGS, data.importAnchorEvent))),
                        React.createElement(DescriptionListItem, { label: t('ANCHOR_EVENT_EXPORT'), className: "mb-4", dataTestId: "anchor-event-export" }, t(getSlug(FSC_EXPORT_ANCHOR_EVENTS, FSC_EXPORT_ANCHOR_EVENTS_SLUGS, data.exportAnchorEvent))),
                        React.createElement(DescriptionListItem, { label: t('ANCHOR_EVENT_REPOSITION'), className: "mb-4", dataTestId: "anchor-event-reposition" }, t(getSlug(FSC_REPOSITION_ANCHOR_EVENTS, FSC_REPOSITION_ANCHOR_EVENTS_SLUGS, data.repositionAnchorEvent))))))),
            React.createElement("div", { className: "mb-4 pt-4" },
                React.createElement("h3", { className: "mb-4" }, t('MATRIX_SETTINGS')),
                React.createElement("div", { className: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" },
                    renderValueField(t('FUEL_PRICE_MIN'), data.fuelPriceMin, 'mb-4', 'fuel-price-min'),
                    renderValueField(t('FUEL_PRICE_MAX'), data.fuelPriceMax, 'mb-4', 'fuel-price-max'),
                    renderValueField(t('FUEL_PRICE_STEP_INTERVAL'), data.fuelPriceStepInterval, 'mb-4', 'fuel-price-step-interval'),
                    renderValueField(`${t('FSC_MIN')} ${unitLabel}`, data.fscMin, 'mb-4', 'fsc-min', unit),
                    renderValueField(`${t('FSC_STEP_INTERVAL')} ${unitLabel}`, data.fscStepInterval, 'mb-4', 'fsc-step-interval', unit))),
            React.createElement("div", { className: "my-4" },
                React.createElement(FSCPreview, { data: {
                        fuelPriceMin: Number(data.fuelPriceMin),
                        fuelPriceMax: Number(data.fuelPriceMax),
                        fuelPriceStepInterval: Number(data.fuelPriceStepInterval),
                        fscMin: Number(data.fscMin),
                        fscStepInterval: Number(data.fscStepInterval),
                        fscStepUnit: unit,
                    } }))));
    }
    function renderVariableAlert() {
        if (!isVariableFSCType()) {
            return null;
        }
        return (React.createElement(Panel, { className: "mb-4" },
            React.createElement(Alert, { type: "warning" }, t('PLEASE_SET_EIA'))));
    }
    if (!isVariableFSCType()) {
        return renderHeading();
    }
    return (React.createElement(CollapsibleItem, { renderTitle: renderHeading, className: "flex-1", hideIcon: true, defaultOpen: defaultOpen },
        renderVariableAlert(),
        renderVariableFSCFields()));
}
